import { END_POINT } from '../end-point';
import { TPostUsersData, TResPostUsers } from '../../types/api';
import { httpService } from '../util/axiosRequests';
import { getCookie } from 'lib/cookie';
import { Item } from 'components/menu/ItemManagement';
import { getAccessTokenFromCookies } from 'lib/utils/setTokensInCookies';

type SystemResponse = {
  data: {
    manualFaq: {
      manualFileName: string;
      faqFileName: string;
      manualFileNameEnglish: string;
      faqFileNameEnglish: string;
      manualFileNameJapanese: string;
      faqFileNameJapanese: string;
    };
    remindTime: string;
    emailTemplates: [
      {
        type: string;
        language: string;
        title: string;
        content: string;
      }
    ];
  };
};
type PolicyResponse = {
  data: {
    terms: string;
    termsEnglish: string;
    termsJapanese: string;
    privacyVersion: string;
    privacyEnglishVersion: string;
    privacyJapaneseVersion: string;
    privacy: string;
    privacyEnglish: string;
    privacyJapanese: string;
    security: string;
    securityEnglish: string;
    securityJapanese: string;
  };
};
type SelectItemsResponse = {
  data: {
    businessPlaces: Item[];
    digitalDevices: Item[];
  };
};
// Visitor 타입 정의
interface VisitorResponse {
  id: number;
  username: string;
  type: string;
  name: string;
  companyName: string;
  email: string;
  phoneNumber: string;
}

// Employee 타입 정의
interface EmployeeResponse {
  id: number;
  username: string;
  name: string;
  email: string;
  phoneNumber: string;
  notificationType: string;
  notificationUpdater: string;
}

// API 응답 타입 정의
interface UsersAPIResponse {
  visitors: VisitorResponse[];
  // employees: EmployeeResponse[];
  visitorNotificationType: string;
}

const settingsAPI = {
  // 항목관리 조회 API
  async getSelectItemsAPI(): Promise<SelectItemsResponse> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getSelectItems(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });
      return response.data;
    } catch (error) {
      // console.error('getSelectItemsAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },

  // 시스템설정 조회 API
  async getSystemAPI(): Promise<SystemResponse> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getSystem(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });
      // console.log('getSystemAPI 조회 성공: ', response);
      return response.data;
    } catch (error) {
      // console.error('getSystemAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },
  // 매뉴얼 업로드 API
  postUploadManualAPI: async (language: string, file: File) => {
    const accessToken = getAccessTokenFromCookies();

    // FormData 객체를 생성하여 파일을 첨부
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await httpService.post<any>(END_POINT.settingController.postUploadManual(language), formData, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer 형식으로 Authorization 헤더에 추가
          'Content-Type': 'multipart/form-data'
        }
      });

      return response;
    } catch (error) {
      // console.error('postUploadManualAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // FAQ 업로드 API
  postUploadFaqAPI: async (language: string, file: any) => {
    const accessToken = getAccessTokenFromCookies();

    // FormData 객체를 생성하여 파일을 첨부
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await httpService.post<any>(END_POINT.settingController.postUploadFaq(language), formData, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer 형식으로 Authorization 헤더에 추가
          'Content-Type': 'multipart/form-data'
        }
      });

      return response;
    } catch (error) {
      // console.error('postUploadFaqAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 리마인드 시간 변경 API
  putUpdateRemindTimeAPI: async (remindTime: string) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const data = {
        remindTime: remindTime
      }
      const response = await httpService.put<any>(END_POINT.settingController.putUpdateRemindTime(), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('putUpdateRemindTimeAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },

  // 정책관리 조회 API
  async getPoliciesAPI(): Promise<PolicyResponse> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getPolicies(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });
      // console.log('getPoliciesAPI 조회 성공: ', response);
      return response.data;
    } catch (error) {
      // console.error('getPoliciesAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },

  // 사용자관리 API
  async getUsersAPI(): Promise<UsersAPIResponse> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getUsers(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });

      return response.data.data;
    } catch (error) {
      // console.error('getUsersAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },
  // 임직원사용자 조회 API
  async getEmployeesAPI(): Promise<EmployeeResponse[]> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getEmployees(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });

      return response.data.data;
    } catch (error) {
      // console.error('getEmployeesAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },
  // 외부사용자 조회 API
  async getVisitorsAPI(): Promise<VisitorResponse> {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getVisitors(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });

      return response.data;
    } catch (error) {
      // console.error('getVisitorsAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },
  // 외부사용자 알림 변경
  putVisitorsNotificationAPI: async (data: any) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.put<any>(END_POINT.settingController.putVisitorsNotification(), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('putVisitorsNotificationAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 사용자 비밀번호 변경
  putUserIdPasswordAPI: async (userId: number, data?: any) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.put<any>(END_POINT.settingController.putUserIdPassword(userId), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('putUserIdPasswordAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 사용자 패널티 변경
  putUserIdPenaltyAPI: async (data: any, userId: number) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.put<any>(END_POINT.settingController.putUserIdPenalty(userId), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('putUserIdPenaltyAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 사용자 알림 변경
  putUserIdNotification: async (data: any, userId: number) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.put<any>(END_POINT.settingController.putUserIdNotification(userId), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('putUserIdNotification 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },

  //** 환경설정 - 권한관리 : 사용자 그룹의 추가, 삭ㅔ 및 그룹에 속한 메뉴와 사용자 관리 **//
  // 권한관리 조회 API
  async getAuthoritiesAPI() {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response: any = await httpService.get(END_POINT.settingController.getAuthorities(), {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // headers에 accessToken 첨부
        }
      });

      return response.data;
    } catch (error) {
      // console.error('getSystemAPI 조회 실패 :', error);
      console.error(error);
      throw error;
    }
  },

  // 그룹 사용자 추가
  postGroupIdUserAPI: async (id: number, data: any) => {
    const accessToken = getAccessTokenFromCookies();
    try {
      const response = await httpService.post<any>(END_POINT.settingController.postGroupIdUser(id), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });
      return response;
    } catch (error) {
      // console.error('postGroupIdUserAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 그룹 사용자 삭제
  deleteGroupIdUserAPI: async (id: number, data: any) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.delete<any>(END_POINT.settingController.deleteGroupIdUser(id), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('deleteGroupIdUserAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },

  // 그룹 메뉴 추가
  postGroupIdMenusAPI: async (id: number, data: any) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.post<any>(END_POINT.settingController.postGroupIdMenus(id), data, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('postGroupIdAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  },
  // 그룹 메뉴 삭제
  deleteGroupIdMenuIdAPI: async (groupId: number, menuId: number) => {
    const accessToken = getAccessTokenFromCookies();

    try {
      const response = await httpService.delete<any>(END_POINT.settingController.deleteGroupIdMenuId(groupId, menuId), null, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}` // Bearer 형식으로 Authorization 헤더에 추가
        }
      });

      return response;
    } catch (error) {
      // console.error('deleteGroupIdMenuIdAPI 호출 중 오류 발생:', error);
      console.error(error);
      throw error;
    }
  }

  //
};

export const {
  getAuthoritiesAPI,
  getSelectItemsAPI,
  getSystemAPI,
  postUploadManualAPI,
  postUploadFaqAPI,
  putUpdateRemindTimeAPI,
  getPoliciesAPI,
  getUsersAPI,
  getEmployeesAPI,
  getVisitorsAPI,
  putVisitorsNotificationAPI,
  putUserIdPenaltyAPI,
  putUserIdPasswordAPI,
  putUserIdNotification,
  postGroupIdUserAPI,
  deleteGroupIdUserAPI,
  postGroupIdMenusAPI,
  deleteGroupIdMenuIdAPI
} = settingsAPI;
